import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { Button } from "react-bootstrap";

import {
  getAllMedicamentos2,
  insertMedicamento2,
  updateMedicamento2,
  deleteMedicamento2,
} from "../../../api/medicamentos2";

import ModalMedicamentos from "../../components/sis/modalopciones/ModalMedicamentos";
import "./Opciones.css"; // ✅ CSS compartido

// 🔹 Filtro por defecto
function DefaultColumnFilter({ column: { filterValue, setFilter, placeholder } }) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={placeholder || "Filtrar..."}
      style={{ minWidth: 100 }}
    />
  );
}

export default function Medicamentos() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const tableRef = useRef(null);

  // 🔹 Cargar medicamentos al montar
  useEffect(() => { loadMedicamentos(); }, []);
  const loadMedicamentos = async () => {
    setLoading(true);
    try {
      const result = await getAllMedicamentos2();
      setData(result || []);
    } catch (err) {
      Swal.fire("Error", "No se pudieron cargar los medicamentos", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Columnas para react-table
  const columns = useMemo(
    () => [
      { Header: "Nombre", accessor: "nombre", minWidth: 200 },
      { Header: "Forma", accessor: "formaFarmaceutica", minWidth: 150 },
      { Header: "Concentración", accessor: "concentracion", minWidth: 130 },
      { Header: "Unidad", accessor: "unidadMedida", width: 100 },
      {
        Header: "Opciones",
        disableFilters: true,
        width: 120,
        Cell: ({ row }) => (
          <div className="d-flex justify-content-center gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleEdit(row.original)}
            >
              <i className="fas fa-pencil-alt"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleDelete(row.original)}
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  // 🔹 Configuración de la tabla
  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageSize: 10 },
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    gotoPage,
    pageCount,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  // 🔹 Handlers
  const handleAdd = () => {
    setSelected({
      nombre: "",
      principioActivo: "",
      formaFarmaceutica: "",
      concentracion: "",
      presentacion: "",
      unidadMedida: "",
      laboratorio: "",
      observacion: "",
    });
    setIsNew(true);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelected(item);
    setIsNew(false);
    setShowModal(true);
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Eliminar Medicamento",
      text: `¿Desea eliminar "${item.nombre}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteMedicamento2(item.idMedicamento);
          Swal.fire("Eliminado", "Medicamento eliminado correctamente", "success");
          loadMedicamentos();
        } catch (err) {
          Swal.fire("Error", "No se pudo eliminar", "error");
        }
      }
    });
  };

  const handleSave = async (data) => {
    setShowModal(false);
    Swal.fire({
      title: "Guardando...",
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
      showConfirmButton: false,
    });
    try {
      if (isNew) await insertMedicamento2(data);
      else await updateMedicamento2(data);
      Swal.fire("Éxito", "Medicamento guardado correctamente", "success");
      loadMedicamentos();
    } catch (err) {
      Swal.fire("Error", `${err}`, "error");
    } finally {
      Swal.close();
    }
  };

  // 🔹 Exportar Excel
  const handleExportExcel = () => {
    if (!data.length)
      return Swal.fire("Sin datos", "No hay medicamentos para exportar", "info");
    const mapped = data.map((m) => ({
      ID: m.idMedicamento,
      Nombre: m.nombre,
      Forma: m.formaFarmaceutica,
      Concentración: m.concentracion,
      Unidad: m.unidadMedida,
      Laboratorio: m.laboratorio,
      Observación: m.observacion,
    }));
    const ws = XLSX.utils.json_to_sheet(mapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Medicamentos");
    XLSX.writeFile(wb, "Medicamentos.xlsx");
  };

  return (
    <div className="opciones-container">
      {/* Encabezado */}
      <div className="opciones-header">
        <h5>💊 Medicamentos</h5>
        <div className="d-flex gap-2">
          <Button size="sm" variant="primary" onClick={handleAdd}>
            Agregar
          </Button>
          <Button size="sm" variant="outline-success" onClick={handleExportExcel}>
            Descargar Excel
          </Button>
        </div>
      </div>

      {/* Buscador */}
      <div className="mb-2 opciones-search">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar medicamento..."
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner-circle"></div>
          <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>
            Cargando...
          </span>
        </div>
      ) : (
        <div className="tabla-scroll">
          <table
            {...getTableProps()}
            className="table table-sm tabla-opciones"
            ref={tableRef}
          >
            <thead>
              {headerGroups.map((hg, i) => (
                <React.Fragment key={i}>
                  <tr {...hg.getHeaderGroupProps()} className="header-row">
                    {hg.headers.map((col) => (
                      <th key={col.id}>{col.render("Header")}</th>
                    ))}
                  </tr>
                  <tr className="filter-row">
                    {hg.headers.map((col) => (
                      <th key={`${col.id}-filter`}>
                        {col.canFilter ? col.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, ri) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={ri}>
                    {row.cells.map((cell, ci) => (
                      <td {...cell.getCellProps()} key={ci}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      <div className="opciones-paginacion">
        <span>
          Página <strong>{pageIndex + 1} de {pageOptions.length || 1}</strong>
        </span>
        <div>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            {"<<"}
          </button>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Anterior
          </button>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Siguiente
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <ModalMedicamentos
          estado={showModal}
          onCloseModal={() => setShowModal(false)}
          onReturnValue={handleSave}
          item={selected}
          isnew={isNew}
        />
      )}
    </div>
  );
}
